// src/pages/AdminPagamentosPendentes.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Grid,
  Avatar,
  Divider,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";

import SearchIcon from "@mui/icons-material/Search";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import UndoIcon from "@mui/icons-material/Undo";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PaymentsIcon from "@mui/icons-material/Payments";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import NumbersIcon from "@mui/icons-material/Numbers";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  addDoc,
} from "firebase/firestore";
import { db } from "../contexts/firebase/config";

const REENVIAR_EMAIL_URL =
  "https://us-central1-gestao-comissoes-sintanet.cloudfunctions.net/reenviarEmailPagamento";

const GERAR_REFERENCIA_URL =
  "https://us-central1-gestao-comissoes-sintanet.cloudfunctions.net/gerarNovaReferencia";

const CONFIRMAR_PAGAMENTO_URL =
  "https://us-central1-gestao-comissoes-sintanet.cloudfunctions.net/confirmarPagamentoCliente";

function money(value) {
  const num = Number(value || 0);
  return `€ ${num.toFixed(2)}`;
}

function normalize(value) {
  return (value || "").toString().toLowerCase().trim();
}

function estadoColor(estado) {
  if (estado === "pago") return "success";
  if (estado === "expirado") return "error";
  return "warning";
}

function fmtDate(value) {
  try {
    if (!value) return "—";
    const d =
      typeof value.toDate === "function" ? value.toDate() : new Date(value);
    return isNaN(d) ? "—" : d.toLocaleString("pt-PT");
  } catch {
    return "—";
  }
}

function parseData(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  const d = new Date(value);
  return isNaN(d) ? null : d;
}

function diasAte(value) {
  const d = parseData(value);
  if (!d) return null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const alvo = new Date(d);
  alvo.setHours(0, 0, 0, 0);

  return Math.ceil((alvo - hoje) / (1000 * 60 * 60 * 24));
}

function parseValidade(validade) {
  if (!validade) return null;
  if (typeof validade.toDate === "function") return validade.toDate();
  if (validade instanceof Date) return validade;

  if (typeof validade === "string") {
    const partes = validade.split("/");
    if (partes.length === 3) {
      const [dia, mes, ano] = partes.map(Number);
      return new Date(ano, mes - 1, dia, 23, 59, 59);
    }

    const d = new Date(validade);
    return isNaN(d) ? null : d;
  }

  return null;
}

function obterEstadoVisual(ref) {
  const estado = ref?.estado || "pendente";
  if (estado === "pago") return "pago";

  const validadeDate = parseValidade(ref?.validade);
  if (!validadeDate) return estado;
  if (validadeDate < new Date()) return "expirado";

  return estado;
}

function obterFiltroValidade(ref) {
  const validadeDate = parseValidade(ref?.validade);

  if (!validadeDate) return "sem_validade";

  const agora = new Date();
  const daqui7Dias = new Date();
  daqui7Dias.setDate(daqui7Dias.getDate() + 7);

  if (validadeDate < agora) return "expiradas";
  if (validadeDate <= daqui7Dias) return "expira_7";
  return "validas";
}

function obterEstadoRenovacao(cliente) {
  const dias = diasAte(cliente.dataFim);

  if (dias === null) {
    return {
      dias: null,
      label: "Sem data",
      filtro: "sem_data",
      color: "default",
    };
  }

  if (dias < 0) {
    return {
      dias,
      label: `Expirado há ${Math.abs(dias)} dia(s)`,
      filtro: "expirado",
      color: "error",
    };
  }

  if (dias === 0) {
    return {
      dias,
      label: "Último dia",
      filtro: "ultimo_dia",
      color: "error",
    };
  }

  if (dias <= 5) {
    return {
      dias,
      label: `Faltam ${dias} dia(s)`,
      filtro: "5",
      color: "error",
    };
  }

  if (dias <= 15) {
    return {
      dias,
      label: `Faltam ${dias} dia(s)`,
      filtro: "15",
      color: "warning",
    };
  }

  if (dias <= 30) {
    return {
      dias,
      label: `Faltam ${dias} dia(s)`,
      filtro: "30",
      color: "info",
    };
  }

  return {
    dias,
    label: `${dias} dia(s)`,
    filtro: "fora",
    color: "success",
  };
}

function deveAparecerNaPagina(cliente) {
  const renovacao = obterEstadoRenovacao(cliente);
  const ref = cliente.referenciaPagamento;

  if (ref) return true;

  return ["30", "15", "5", "ultimo_dia"].includes(renovacao.filtro);
}

function csvValue(value) {
  const safe = value === undefined || value === null ? "" : String(value);
  return `"${safe.replace(/"/g, '""')}"`;
}

function normalizarTelefone(value) {
  return (value || "")
    .toString()
    .replace(/\s+/g, "")
    .replace(/[^\d+]/g, "");
}

function showSuccess(message) {
  enqueueSnackbar(message, { variant: "success" });
}

function showError(message) {
  enqueueSnackbar(message, { variant: "error" });
}

function showWarning(message) {
  enqueueSnackbar(message, { variant: "warning" });
}
export default function AdminPagamentosPendentes() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const ui = {
    bg: isDark ? "#020617" : "#f6f8fc",
    card: isDark ? "rgba(15,23,42,0.88)" : "#ffffff",
    soft: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
    border: isDark ? "rgba(255,255,255,0.09)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
  };

  const [clientes, setClientes] = useState([]);
  const [, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setLoadingLogs] = useState(true);

  const [pesquisa, setPesquisa] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("todos");
  const [validadeFiltro, setValidadeFiltro] = useState("todas");
  const [renovacaoFiltro, setRenovacaoFiltro] = useState("todos");
  const [empresas, setEmpresas] = useState({});

  const [updatingId, setUpdatingId] = useState(null);
  const [reenviandoId, setReenviandoId] = useState(null);
  const [gerandoReferenciaId, setGerandoReferenciaId] = useState(null);
  const [copiado, setCopiado] = useState("");
  const [openNovaReferencia, setOpenNovaReferencia] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [novoValorReferencia, setNovoValorReferencia] = useState("");

  function limparFiltros() {
    setPesquisa("");
    setEstadoFiltro("todos");
    setValidadeFiltro("todas");
    setRenovacaoFiltro("todos");
    showSuccess("Filtros limpos.");
  }

  useEffect(() => {
  const unsub = onSnapshot(collection(db, "empresas"), (snap) => {
    const mapa = {};

    snap.forEach((doc) => {
      mapa[doc.id] = doc.data().nome || doc.data().nomeEmpresa || "Sem nome";
    });

    setEmpresas(mapa);
  });

  return unsub;
}, []);

  useEffect(() => {
    setLoading(true);

    const unsub = onSnapshot(
      collection(db, "clientes"),
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => deveAparecerNaPagina(c));

        setClientes(list);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, []);

  useEffect(() => {
    setLoadingLogs(true);

    const q = query(
      collection(db, "pagamentosLogs"),
      orderBy("criadoEm", "desc"),
      limit(12)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingLogs(false);
      },
      (error) => {
        console.error(error);
        setLoadingLogs(false);
      }
    );

    return unsub;
  }, []);

  const clientesComEstadoVisual = useMemo(() => {
    return clientes.map((cliente) => {
      const ref = cliente.referenciaPagamento || {};
      const renovacao = obterEstadoRenovacao(cliente);

      return {
        ...cliente,
        estadoRenovacao: renovacao,
        referenciaPagamento: {
          ...ref,
          estadoVisual: obterEstadoVisual(ref),
          validadeVisual: obterFiltroValidade(ref),
        },
      };
    });
  }, [clientes]);

  const filtrados = useMemo(() => {
    const termo = normalize(pesquisa);

    return clientesComEstadoVisual.filter((c) => {
      const ref = c.referenciaPagamento || {};
      const estado = ref.estadoVisual || "pendente";
      const validade = ref.validadeVisual || "sem_validade";
      const renovacao = c.estadoRenovacao?.filtro || "sem_data";

      const matchEstado = estadoFiltro === "todos" || estado === estadoFiltro;
      const matchValidade =
        validadeFiltro === "todas" || validade === validadeFiltro;

      const matchRenovacao =
        renovacaoFiltro === "todos" ||
        renovacao === renovacaoFiltro ||
        (renovacaoFiltro === "30" && ["30", "15", "5", "ultimo_dia"].includes(renovacao)) ||
        (renovacaoFiltro === "15" && ["15", "5", "ultimo_dia"].includes(renovacao)) ||
        (renovacaoFiltro === "5" && ["5", "ultimo_dia"].includes(renovacao));

      const matchPesquisa =
        !termo ||
        normalize(c.nome).includes(termo) ||
        normalize(c.email).includes(termo) ||
        normalize(c.nif).includes(termo) ||
        normalize(c.plano).includes(termo) ||
        normalize(c.estado).includes(termo) ||
        normalize(ref.entidade).includes(termo) ||
        normalize(ref.referencia).includes(termo) ||
        normalize(ref.estado).includes(termo) ||
        normalize(ref.estadoVisual).includes(termo);

      return matchEstado && matchValidade && matchRenovacao && matchPesquisa;
    });
  }, [
    clientesComEstadoVisual,
    pesquisa,
    estadoFiltro,
    validadeFiltro,
    renovacaoFiltro,
  ]);

  const totalPendente = useMemo(() => {
    return filtrados
      .filter((c) => c.referenciaPagamento?.estadoVisual === "pendente")
      .reduce((acc, c) => acc + Number(c.referenciaPagamento?.valor || 0), 0);
  }, [filtrados]);

  const totalPago = useMemo(() => {
    return filtrados
      .filter((c) => c.referenciaPagamento?.estadoVisual === "pago")
      .reduce((acc, c) => acc + Number(c.referenciaPagamento?.valor || 0), 0);
  }, [filtrados]);

  const totalExpirado = useMemo(() => {
    return filtrados
      .filter((c) => c.referenciaPagamento?.estadoVisual === "expirado")
      .reduce((acc, c) => acc + Number(c.referenciaPagamento?.valor || 0), 0);
  }, [filtrados]);

  const contagem = useMemo(() => {
    return {
      todos: filtrados.length,
      pendentes: filtrados.filter(
        (c) => c.referenciaPagamento?.estadoVisual === "pendente"
      ).length,
      pagos: filtrados.filter(
        (c) => c.referenciaPagamento?.estadoVisual === "pago"
      ).length,
      expirados: filtrados.filter(
        (c) => c.referenciaPagamento?.estadoVisual === "expirado"
      ).length,
      aviso30: filtrados.filter((c) =>
        ["30", "15", "5", "ultimo_dia"].includes(c.estadoRenovacao?.filtro)
      ).length,
      aviso15: filtrados.filter((c) =>
        ["15", "5", "ultimo_dia"].includes(c.estadoRenovacao?.filtro)
      ).length,
      aviso5: filtrados.filter((c) =>
        ["5", "ultimo_dia"].includes(c.estadoRenovacao?.filtro)
      ).length,
      ultimoDia: filtrados.filter(
        (c) => c.estadoRenovacao?.filtro === "ultimo_dia"
      ).length,
    };
  }, [filtrados]);

  async function registrarLogManual(cliente, acao, detalhe) {
    const ref = cliente.referenciaPagamento || {};

    try {
      await addDoc(collection(db, "pagamentosLogs"), {
        clienteId: cliente.id,
        clienteNome: cliente.nome || "",
        acao,
        detalhe,
        origem: "painel_admin",
        referencia: ref.referencia || null,
        entidade: ref.entidade || null,
        valor: ref.valor || null,
        provider: ref.provider || null,
        criadoEm: serverTimestamp(),
      });
    } catch (error) {
      console.error("Erro ao registar log:", error);
    }
  }

  async function copiarTexto(texto, tipo) {
    if (!texto) return;

    try {
      await navigator.clipboard.writeText(String(texto));
      setCopiado(tipo);
      setTimeout(() => setCopiado(""), 1500);
      showSuccess("Copiado com sucesso.");
    } catch (error) {
      console.error(error);
      showError("Não foi possível copiar.");
    }
  }

  async function copiarDadosPagamento(cliente) {
    const ref = cliente.referenciaPagamento || {};

    const texto = `
Cliente: ${cliente.nome || "-"}
Email: ${cliente.email || "-"}
Plano: ${cliente.plano || "-"}
Renovação: ${cliente.estadoRenovacao?.label || "-"}
Entidade: ${ref.entidade || "-"}
Referência: ${ref.referencia || "-"}
Valor: ${money(ref.valor)}
Validade: ${ref.validade || "-"}
    `.trim();

    await copiarTexto(texto, `dados-${cliente.id}`);

    await registrarLogManual(
      cliente,
      "DADOS_PAGAMENTO_COPIADOS",
      "Dados de pagamento copiados pelo admin."
    );
  }
    async function abrirWhatsApp(cliente) {
    const ref = cliente.referenciaPagamento || {};

    const telefone = normalizarTelefone(
      cliente.telefone || cliente.contacto || cliente.phone || ""
    );

    if (!telefone) {
      showWarning("Cliente sem telefone/contacto.");
      return;
    }

    const mensagem = `
Olá ${cliente.nome || ""},

O seu plano está próximo da renovação.

Plano: ${cliente.plano || "-"}
Estado da renovação: ${cliente.estadoRenovacao?.label || "-"}

Dados de pagamento:
Entidade: ${ref.entidade || "-"}
Referência: ${ref.referencia || "-"}
Valor: ${money(ref.valor)}
Validade: ${ref.validade || "-"}

Obrigado.
    `.trim();

    const url = `https://wa.me/${telefone.replace(
      "+",
      ""
    )}?text=${encodeURIComponent(mensagem)}`;

    window.open(url, "_blank");

    await registrarLogManual(
      cliente,
      "WHATSAPP_ABERTO",
      "Mensagem de pagamento aberta no WhatsApp pelo admin."
    );
  }

  function abrirNovaReferencia(cliente) {
  setClienteSelecionado(cliente);

  setNovoValorReferencia(
    cliente.referenciaPagamento?.valor || ""
  );

  setOpenNovaReferencia(true);
}

async function confirmarNovaReferencia() {
  if (!clienteSelecionado) return;

  try {
    setGerandoReferenciaId(clienteSelecionado.id);

    const response = await fetch(
      `${GERAR_REFERENCIA_URL}?clienteId=${clienteSelecionado.id}&valor=${novoValorReferencia}`,
      {
        method: "GET",
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || "Erro ao gerar referência.");
    }

    showSuccess("Nova referência criada com sucesso.");

    setOpenNovaReferencia(false);
    setClienteSelecionado(null);
    setNovoValorReferencia("");

  } catch (error) {
    console.error(error);
    showError(error.message);
  } finally {
    setGerandoReferenciaId(null);
  }
}

  function exportarCSV() {
    if (filtrados.length === 0) {
      showWarning("Não existem pagamentos para exportar.");
      return;
    }

    const headers = [
      "Cliente",
      "Email",
      "NIF",
      "Plano",
      "Renovacao",
      "Data Fim",
      "Dias Restantes",
      "Entidade",
      "Referencia",
      "Valor",
      "Validade",
      "Estado",
      "Pago em",
      "Provider",
      "Metodo",
      "Ultimo reenvio",
    ];

    const rows = filtrados.map((cliente) => {
      const ref = cliente.referenciaPagamento || {};
      const renovacao = cliente.estadoRenovacao || {};

      return [
        cliente.nome || "",
        cliente.email || "",
        cliente.nif || "",
        cliente.plano || "",
        renovacao.label || "",
        fmtDate(cliente.dataFim),
        renovacao.dias ?? "",
        ref.entidade || "",
        ref.referencia || "",
        Number(ref.valor || 0).toFixed(2),
        ref.validade || "",
        ref.estadoVisual || ref.estado || "pendente",
        ref.estadoVisual === "pago" ? fmtDate(ref.pagoEm) : "",
        ref.provider || "",
        ref.metodo || "",
        fmtDate(ref.ultimoReenvioEm),
      ];
    });

    const csvContent = [
      headers.map(csvValue).join(";"),
      ...rows.map((row) => row.map(csvValue).join(";")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const dataHoje = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = `pagamentos-renovacoes-${dataHoje}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
    showSuccess("CSV exportado com sucesso.");
  }

  function exportarPDF() {
    if (filtrados.length === 0) {
      showWarning("Não existem pagamentos para exportar.");
      return;
    }

    const pdf = new jsPDF("landscape", "mm", "a4");
    const hoje = new Date();
    const dataHoje = hoje.toLocaleDateString("pt-PT");

    pdf.setFillColor(17, 24, 39);
    pdf.rect(0, 0, 297, 34, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");
    pdf.text("Sintanet", 14, 14);

    pdf.setFontSize(11);
    pdf.setFont("helvetica", "normal");
    pdf.text("Relatório de Pagamentos e Renovações", 14, 23);

    pdf.setTextColor(15, 23, 42);
    pdf.setFontSize(10);
    pdf.text(`Gerado em: ${dataHoje}`, 14, 44);
    pdf.text(`Registos exportados: ${filtrados.length}`, 14, 51);

    pdf.setFont("helvetica", "bold");
    pdf.text(`Total pendente: ${money(totalPendente)}`, 75, 44);
    pdf.text(`Total pago: ${money(totalPago)}`, 145, 44);
    pdf.text(`Total expirado: ${money(totalExpirado)}`, 205, 44);

    const rows = filtrados.map((cliente) => {
      const ref = cliente.referenciaPagamento || {};
      const renovacao = cliente.estadoRenovacao || {};

      return [
        cliente.nome || "—",
        cliente.email || "—",
        cliente.plano || "—",
        renovacao.label || "—",
        fmtDate(cliente.dataFim),
        ref.entidade || "—",
        ref.referencia || "—",
        money(ref.valor),
        ref.estadoVisual || ref.estado || "pendente",
      ];
    });

    autoTable(pdf, {
      startY: 60,
      head: [
        [
          "Cliente",
          "Email",
          "Plano",
          "Renovação",
          "Data Fim",
          "Entidade",
          "Referência",
          "Valor",
          "Estado",
        ],
      ],
      body: rows,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [17, 24, 39],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      margin: { left: 10, right: 10 },
    });

    pdf.save(`relatorio-pagamentos-renovacoes-${hoje.toISOString().slice(0, 10)}.pdf`);

    showSuccess("PDF exportado com sucesso.");
  }

  async function marcarComoPago(cliente) {
    if (
      !window.confirm(
        `Confirmar pagamento de ${cliente.nome || "cliente"}?\n\n` +
          `Isto vai marcar o pagamento como pago, renovar o cliente por +1 ano e enviar email de confirmação.`
      )
    ) {
      return;
    }

    try {
      setUpdatingId(cliente.id);

      const response = await fetch(
        `${CONFIRMAR_PAGAMENTO_URL}?clienteId=${encodeURIComponent(cliente.id)}`,
        { method: "GET" }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Erro ao confirmar pagamento.");
      }

      showSuccess(
        data.novaDataFim
          ? `Pagamento confirmado. Renovação ativa até ${data.novaDataFim}.`
          : "Pagamento confirmado e renovação aplicada."
      );
    } catch (error) {
      console.error(error);
      showError(error.message || "Erro ao confirmar pagamento.");
    } finally {
      setUpdatingId(null);
    }
  }

  async function reverterParaPendente(cliente) {
    if (
      !window.confirm(
        `Reverter pagamento de ${cliente.nome || "cliente"} para pendente?`
      )
    ) {
      return;
    }

    try {
      setUpdatingId(cliente.id);

      await updateDoc(doc(db, "clientes", cliente.id), {
        "referenciaPagamento.estado": "pendente",
        "referenciaPagamento.pagoEm": null,
      });

      await registrarLogManual(
        cliente,
        "PAGAMENTO_REVERTIDO_PARA_PENDENTE",
        "Pagamento revertido manualmente para pendente pelo admin."
      );

      showWarning("Pagamento revertido para pendente.");
    } catch (error) {
      console.error(error);
      showError("Erro ao reverter pagamento para pendente.");
    } finally {
      setUpdatingId(null);
    }
  }

  async function reenviarEmailPagamento(cliente) {
    if (
      !window.confirm(
        `Reenviar email de pagamento para ${cliente.nome || "cliente"}?`
      )
    ) {
      return;
    }

    try {
      setReenviandoId(cliente.id);

      await fetch(
        `${REENVIAR_EMAIL_URL}?clienteId=${encodeURIComponent(cliente.id)}`,
        {
          method: "GET",
          mode: "no-cors",
        }
      );

      showSuccess("Pedido de reenvio enviado.");
    } catch (error) {
      console.error(error);
      showError("Erro ao reenviar email.");
    } finally {
      setReenviandoId(null);
    }
  }
    const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        height: "100%",
        borderRadius: "24px",
        bgcolor: alpha(color, isDark ? 0.13 : 0.065),
        border: `1px solid ${alpha(color, 0.16)}`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 48,
            height: 48,
            borderRadius: "17px",
            bgcolor: alpha(color, 0.14),
            color,
          }}
        >
          {icon}
        </Avatar>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 900, color }}>
            {title}
          </Typography>

          <Typography
            sx={{
              mt: 0.4,
              fontSize: { xs: "1.65rem", md: "1.95rem" },
              fontWeight: 950,
              letterSpacing: "-0.05em",
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>

          <Typography sx={{ mt: 0.5, fontSize: 13, color: "text.secondary" }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%", bgcolor: ui.bg, p: { xs: 0.5, md: 1 } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2.5}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography sx={{ fontSize: 14, color: ui.muted, fontWeight: 800 }}>
            Gestão financeira
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              fontSize: { xs: "2rem", md: "2.75rem" },
              fontWeight: 950,
              letterSpacing: "-0.06em",
              lineHeight: 1,
              color: ui.text,
            }}
          >
            Pagamentos e Renovações
          </Typography>

          <Typography sx={{ mt: 1.2, color: ui.muted }}>
            Mostra pagamentos existentes e clientes próximos da renovação aos 30, 15, 5 dias e último dia.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportarPDF}
            sx={{
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 900,
              px: 2.2,
              height: 44,
              bgcolor: ui.card,
            }}
          >
            Exportar PDF
          </Button>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportarCSV}
            sx={{
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 900,
              px: 2.2,
              height: 44,
            }}
          >
            Exportar CSV
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2.2}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Registos"
            value={filtrados.length}
            subtitle="Pagamentos/renovações visíveis"
            icon={<CreditCardIcon />}
            color={theme.palette.primary.main}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avisos 30 dias"
            value={contagem.aviso30}
            subtitle="Clientes até 30 dias"
            icon={<EventAvailableIcon />}
            color={theme.palette.info.main}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avisos 5 dias"
            value={contagem.aviso5}
            subtitle="Clientes urgentes"
            icon={<WarningAmberIcon />}
            color={theme.palette.error.main}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Pendente"
            value={money(totalPendente)}
            subtitle={`${contagem.pendentes} pagamento(s)`}
            icon={<PendingActionsIcon />}
            color={theme.palette.warning.main}
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: { xs: 2, md: 2.5 },
          borderRadius: "26px",
          bgcolor: ui.card,
          border: `1px solid ${ui.border}`,
          boxShadow: isDark
            ? "0 18px 50px rgba(0,0,0,0.18)"
            : "0 18px 45px rgba(15,23,42,0.055)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            fullWidth
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            placeholder="Pesquisar cliente, email, NIF, entidade ou referência..."
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 54,
                borderRadius: "16px",
                bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#ffffff",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", md: 190 } }}>
            <InputLabel>Estado</InputLabel>

            <Select
              value={estadoFiltro}
              label="Estado"
              onChange={(e) => setEstadoFiltro(e.target.value)}
              sx={{
                height: 54,
                borderRadius: "16px",
                bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#ffffff",
              }}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="pendente">Pendentes</MenuItem>
              <MenuItem value="pago">Pagos</MenuItem>
              <MenuItem value="expirado">Expirados</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: { xs: "100%", md: 210 } }}>
            <InputLabel>Renovação</InputLabel>

            <Select
              value={renovacaoFiltro}
              label="Renovação"
              onChange={(e) => setRenovacaoFiltro(e.target.value)}
              sx={{
                height: 54,
                borderRadius: "16px",
                bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#ffffff",
              }}
            >
              <MenuItem value="todos">Todas</MenuItem>
              <MenuItem value="30">Até 30 dias</MenuItem>
              <MenuItem value="15">Até 15 dias</MenuItem>
              <MenuItem value="5">Até 5 dias</MenuItem>
              <MenuItem value="ultimo_dia">Último dia</MenuItem>
              <MenuItem value="expirado">Expirados</MenuItem>
              <MenuItem value="sem_data">Sem data</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: { xs: "100%", md: 210 } }}>
            <InputLabel>Validade Ref.</InputLabel>

            <Select
              value={validadeFiltro}
              label="Validade Ref."
              onChange={(e) => setValidadeFiltro(e.target.value)}
              sx={{
                height: 54,
                borderRadius: "16px",
                bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#ffffff",
              }}
            >
              <MenuItem value="todas">Todas</MenuItem>
              <MenuItem value="validas">Válidas</MenuItem>
              <MenuItem value="expira_7">Expiram em 7 dias</MenuItem>
              <MenuItem value="expiradas">Expiradas</MenuItem>
              <MenuItem value="sem_validade">Sem validade</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<FilterAltOffIcon />}
            onClick={limparFiltros}
            sx={{
              height: 54,
              borderRadius: "16px",
              textTransform: "none",
              fontWeight: 900,
              bgcolor: ui.card,
              whiteSpace: "nowrap",
            }}
          >
            Limpar
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          borderRadius: "26px",
          overflow: "hidden",
          border: `1px solid ${ui.border}`,
          bgcolor: ui.card,
          boxShadow: isDark
            ? "0 18px 55px rgba(0,0,0,0.18)"
            : "0 18px 45px rgba(15,23,42,0.055)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
          sx={{ px: 2.5, py: 2.2 }}
        >
          <Stack direction="row" spacing={1.4} alignItems="center">
            <Avatar
              sx={{
                width: 46,
                height: 46,
                borderRadius: "16px",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            >
              <PaymentsIcon />
            </Avatar>

            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: "1.1rem" }}>
                Lista de pagamentos e renovações
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {filtrados.length} resultado(s) encontrados.
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider />

        <TableContainer>
          <Table sx={{ minWidth: 1250 }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#f8fafc",
                }}
              >
                <TableCell sx={{ fontWeight: 950 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Renovação</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Entidade</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Referência</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Validade</TableCell>
                <TableCell sx={{ fontWeight: 950 }} align="right">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography sx={{ fontWeight: 900 }}>
                        Nenhum pagamento ou renovação encontrado.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>
                        Tenta limpar os filtros ou pesquisar por outro termo.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filtrados.map((cliente) => {
                  const ref = cliente.referenciaPagamento || {};
                  const estadoVisual = ref.estadoVisual || "pendente";
                  const isPago = estadoVisual === "pago";
                  const renovacao = cliente.estadoRenovacao || {};

                  return (
                    <TableRow key={cliente.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1.3} alignItems="center">
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: "14px",
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                              fontWeight: 900,
                            }}
                          >
                            {(cliente.nome || "C").charAt(0).toUpperCase()}
                          </Avatar>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={950} noWrap>
                              {cliente.nome || "—"}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" noWrap>
  {cliente.email || "—"}
</Typography>

<Typography
  variant="caption"
  sx={{
    display: "block",
    color: "primary.main",
    fontWeight: 700,
    mt: 0.3,
  }}
  noWrap
>
  🏢 {empresas[cliente.empresaId] || "Empresa não encontrada"}
</Typography>

<Typography
  variant="caption"
  color="text.secondary"
  sx={{ display: "block" }}
  noWrap
>
  Plano: {cliente.plano || "—"}
</Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={renovacao.label || "—"}
                          color={renovacao.color || "default"}
                          sx={{ borderRadius: 999, fontWeight: 900 }}
                        />

                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          Data fim: {fmtDate(cliente.dataFim)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AccountBalanceIcon fontSize="small" color="disabled" />
                          <Typography fontWeight={800}>{ref.entidade || "—"}</Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <NumbersIcon fontSize="small" color="disabled" />
                          <Typography fontWeight={900}>{ref.referencia || "—"}</Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={950}>{money(ref.valor)}</Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={estadoVisual}
                          color={estadoColor(estadoVisual)}
                          sx={{ borderRadius: 999, fontWeight: 900 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={800}>{ref.validade || "—"}</Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                          <Tooltip title="Copiar dados">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ContentCopyIcon />}
                              onClick={() => copiarDadosPagamento(cliente)}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                            >
                              {copiado === `dados-${cliente.id}` ? "Copiado" : "Copiar"}
                            </Button>
                          </Tooltip>

                          <Tooltip title="Enviar por WhatsApp">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<WhatsAppIcon />}
                              onClick={() => abrirWhatsApp(cliente)}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                            >
                              WhatsApp
                            </Button>
                          </Tooltip>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EmailIcon />}
                            onClick={() => reenviarEmailPagamento(cliente)}
                            disabled={reenviandoId === cliente.id || !cliente.referenciaPagamento}
                            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                          >
                            {reenviandoId === cliente.id ? "..." : "Email"}
                          </Button>

                          {!isPago ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => marcarComoPago(cliente)}
                              disabled={updatingId === cliente.id || !cliente.referenciaPagamento}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                            >
                              Pago
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<UndoIcon />}
                              onClick={() => reverterParaPendente(cliente)}
                              disabled={updatingId === cliente.id}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                            >
                              Reverter
                            </Button>
                          )}

                          {!cliente.referenciaPagamento && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<AutorenewIcon />}
                              onClick={() => abrirNovaReferencia(cliente)}
                              disabled={gerandoReferenciaId === cliente.id}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                            >
                              Gerar Ref.
                            </Button>
                          )}

                          <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<AutorenewIcon />}
                              onClick={() => abrirNovaReferencia(cliente)}
                              sx={{
                                  borderRadius: 999,
                                  textTransform: "none",
                                  fontWeight: 900,
                              }}
                          >
                              Nova Referência
                          </Button>

                          <Button
                            component={RouterLink}
                            to={`/admin/cliente/${cliente.id}`}
                            size="small"
                            variant="contained"
                            startIcon={<VisibilityIcon />}
                            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                          >
                            Ver
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog
    open={openNovaReferencia}
    onClose={() => setOpenNovaReferencia(false)}
    maxWidth="sm"
    fullWidth
>
    <DialogTitle>
        Nova Referência
    </DialogTitle>

    <DialogContent>

        <TextField
            fullWidth
            margin="normal"
            label="Montante (€)"
            type="number"
            value={novoValorReferencia}
            onChange={(e) =>
                setNovoValorReferencia(e.target.value)
            }
        />

    </DialogContent>

    <DialogActions>

        <Button
            onClick={() => setOpenNovaReferencia(false)}
        >
            Cancelar
        </Button>

        <Button
    variant="contained"
    onClick={confirmarNovaReferencia}
    disabled={gerandoReferenciaId !== null}
>
    {gerandoReferenciaId ? "A gerar..." : "Criar Referência"}
    </Button>

    </DialogActions>

</Dialog>
    </Box>
  );
}
